import { ArrowsClockwiseIcon, DownloadSimpleIcon, InfoIcon } from "@phosphor-icons/react";

function Stat({ label, value, sub, testId, accent }) {
  return (
    <div className="border border-zinc-200 bg-white p-4" data-testid={testId}>
      <div className="overline mb-1.5">{label}</div>
      <div className={`font-heading font-black text-3xl font-mono-num ${accent || "text-zinc-950"}`}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-400 mt-1">{sub}</div>}
    </div>
  );
}

export function ReportPanel({ report, onRefresh, onBackfill, busy }) {
  if (!report) return null;
  const { intros, cross_group_duplicates: dup, leads, keyword_tuning: kt } = report;
  return (
    <div className="border border-zinc-200 bg-zinc-50/60 p-5 mb-6" data-testid="grok-report-panel">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
        <div className="flex-1">
          <div className="overline mb-1">Last {report.window_days} days · generated {new Date(report.generated_at).toLocaleString()}</div>
          <div className="font-heading font-black text-lg">Dry-run report</div>
        </div>
        <div className="flex gap-2">
          <button data-testid="grok-backfill-btn" disabled={busy} onClick={onBackfill}
            className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 bg-white hover:bg-zinc-50 disabled:opacity-40 inline-flex items-center gap-1.5">
            <DownloadSimpleIcon size={14} weight="bold" /> Backfill leads (90d)
          </button>
          <button data-testid="grok-report-refresh-btn" disabled={busy} onClick={onRefresh}
            className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-40 inline-flex items-center gap-1.5">
            <ArrowsClockwiseIcon size={14} weight="bold" /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <Stat testId="report-intros-suppressed" label="Intros that would be suppressed" value={intros.would_be_suppressed}
          sub={`${intros.sent_in_window} intros to ${intros.unique_contacts} contacts`} accent="text-[#002FA7]" />
        <Stat testId="report-dup-alerts" label="Duplicate alerts (24h cross-group)" value={dup.duplicate_alerts_sent}
          sub={`${dup.duplicate_messages} duplicate of ${dup.messages_scanned} messages`} />
        <Stat testId="report-needs-answer" label="Leads needing an answer" value={leads.needs_answer}
          sub={`${leads.acked_awaiting_reply} acked, waiting for reply`} accent={leads.needs_answer ? "text-[#FF3B30]" : undefined} />
        <Stat testId="report-unanswered" label="Contacts with unanswered msgs" value={leads.contacts_with_unanswered}
          sub={`${leads.contacts_stopped_at_cap} stopped at cap · ${leads.opted_out_contacts} opted out`} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat testId="report-leads-total" label="Leads in pipeline" value={leads.total} />
        <Stat testId="report-email-dupes" label="Duplicate emails blocked" value={report.email_duplicates_blocked} />
        <Stat testId="report-tuning" label="Scores tuning would change" value={kt.messages_adjusted} sub={`${kt.would_drop_priority} would change priority`} />
        <Stat testId="report-outbox" label="Bot replies logged (dry-run)" value={report.outbox.dry_run || 0} sub={`${report.outbox.queued || 0} queued live`} />
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="report-status-breakdown">
        {Object.entries(leads.by_status).map(([s, n]) => (
          <span key={s} className="px-2 py-1 text-xs border border-zinc-200 bg-white font-mono-num">{s} · {n}</span>
        ))}
      </div>
      {report.notes.map((n, i) => (
        <div key={i} data-testid={`report-note-${i}`} className="text-xs text-zinc-600 bg-[#FFCC00]/15 border border-[#FFCC00]/50 p-2.5 mb-2 flex gap-2">
          <InfoIcon size={14} weight="bold" className="flex-shrink-0 mt-0.5" /> {n}
        </div>
      ))}
    </div>
  );
}
